import { useMemo, useState, type FormEvent } from 'react';
import { revenue } from '../../data/seedData';
import { BarChart, Donut } from '../../components/ui/Charts';
import { Badge, Card, MiniStat, SectionTitle } from '../../components/ui/Primitives';
import { Dialog } from '../../components/ui/Dialog';
import { useToast } from '../../components/ui/ToastProvider';
import { useApp } from '../../lib/app/AppContext';

type Tab = 'Overview' | 'Acquisition' | 'Retention' | 'Revenue';

export function AnalyticsPage() {
  const { pushToast } = useToast();
  const { reports, createReport } = useApp();
  const [tab, setTab] = useState<Tab>('Overview');
  const [reportOpen, setReportOpen] = useState(false);

  const summary = useMemo(() => ({
    Overview: { title: 'ARR expansion', value: '$5.14M ARR', change: '+18.4% YoY' },
    Acquisition: { title: 'Acquisition efficiency', value: '84,209 visitors', change: '+12.7% MoM' },
    Retention: { title: 'Cohort retention', value: '78.4% retained', change: '+4.9 pts' },
    Revenue: { title: 'Revenue quality', value: '$428.6K MRR', change: '+18.4% YoY' },
  }[tab]), [tab]);

  function generateReport(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const rawName = data.get('name');
    const rawPeriod = data.get('period');
    const rawFormat = data.get('format');
    if (typeof rawName !== 'string' || !rawName.trim()) return;
    if (typeof rawPeriod !== 'string' || typeof rawFormat !== 'string') return;

    const report = createReport({
      name: rawName.trim(),
      tab,
      period: rawPeriod,
      format: rawFormat,
    });

    const payload = JSON.stringify({
      ...report,
      summary,
      revenue,
    }, null, 2);
    const url = URL.createObjectURL(new Blob([payload], { type: 'application/json' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = report.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '.json';
    anchor.click();
    URL.revokeObjectURL(url);

    setReportOpen(false);
    pushToast('Analytics report generated', { message: report.name, tone: 'success' });
  }

  return (
    <div className="page-stack">
      <div className="hero-row">
        <div>
          <span className="page-kicker">Analytics</span>
          <h1>Growth intelligence</h1>
          <p>Acquisition, activation, retention, monetization, and cohort behavior in one operating view.</p>
        </div>
        <button className="btn primary" onClick={() => setReportOpen(true)}>New report</button>
      </div>

      <div className="segmented" role="tablist">
        {(['Overview','Acquisition','Retention','Revenue'] as Tab[]).map((item) => (
          <button role="tab" aria-selected={tab === item} className={tab === item ? 'active' : ''} onClick={() => setTab(item)} key={item}>{item}</button>
        ))}
      </div>

      <div className="analytics-grid">
        <Card className="wide-card">
          <SectionTitle eyebrow={tab} title={summary.title} action={<Badge tone="positive">{summary.change}</Badge>} />
          <div className="analytics-head"><strong>{summary.value}</strong><span>Interactive metrics for the selected view</span></div>
          <BarChart data={revenue} />
        </Card>
        <Card>
          <SectionTitle eyebrow="Funnel" title={tab === 'Retention' ? 'Lifecycle depth' : 'Activation'} />
          <div className="funnel">
            <div style={{ width: '100%' }}>Visited <b>84,209</b></div>
            <div style={{ width: '82%' }}>Signed up <b>28,441</b></div>
            <div style={{ width: '62%' }}>Activated <b>17,721</b></div>
            <div style={{ width: '39%' }}>Paid <b>6,983</b></div>
          </div>
        </Card>
      </div>

      <div className="three-grid">
        <Card>
          <SectionTitle title="Cohort retention" />
          <div className="cohort-grid">{Array.from({ length: 36 }, (_, index) => <span key={index} style={{ opacity: 0.18 + ((index * 17) % 70) / 100 }} />)}</div>
        </Card>
        <Card>
          <SectionTitle title="Channel mix" />
          <div className="donut-row compact"><Donut value={62} label="organic" sublabel="Primary acquisition" /></div>
          <div className="legend-list">
            <span><i className="legend-dot" /> Organic search <b>62%</b></span>
            <span><i className="legend-dot alt" /> Direct <b>21%</b></span>
            <span><i className="legend-dot dim" /> Partner <b>17%</b></span>
          </div>
        </Card>
        <Card>
          <SectionTitle title="Operating metrics" />
          <div className="metric-stack">
            <MiniStat label="CAC" value="$218" detail="-14% QoQ" />
            <MiniStat label="LTV" value="$3,482" detail="+9% QoQ" />
            <MiniStat label="Payback" value="5.2 mo" detail="-0.8 mo" />
            <MiniStat label="Gross margin" value="82.7%" detail="+1.4 pts" />
          </div>
        </Card>
      </div>

      <Card>
        <SectionTitle eyebrow="Saved output" title="Recent reports" />
        {reports.length ? (
          <div className="report-list">
            {reports.slice(0, 5).map((report) => (
              <div key={report.id}>
                <span><strong>{report.name}</strong><small>{report.tab} · {report.period} · {report.format}</small></span>
                <time>{new Date(report.createdAt).toLocaleString()}</time>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state compact"><strong>No generated reports yet</strong><span>Create one from the button above.</span></div>
        )}
      </Card>

      <Dialog open={reportOpen} title="Create analytics report" onClose={() => setReportOpen(false)}>
        <form className="form-stack" onSubmit={generateReport}>
          <label>Report name<input name="name" defaultValue={tab + ' performance report'} required /></label>
          <label>Period<select name="period"><option>Last 30 days</option><option>Quarter to date</option><option>Year to date</option></select></label>
          <label>Format<select name="format"><option>Executive summary</option><option>Detailed analysis</option></select></label>
          <button className="btn primary" type="submit">Generate & download</button>
        </form>
      </Dialog>
    </div>
  );
}
